import React, { useEffect, useState } from "react";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import { fetchHcfDetails } from "./api";
import { ShowError } from "src/components/error";
import { WorkerRate } from "./workerRate";
import { Stack, Tooltip } from "@mui/material";
import { Text } from "@src/lib/ui-react";
import { logEvent } from "src/appV2/lib/analytics";
import { showSuccessToast } from "@src/appV2/lib/Notifications";
import { ADMIN_EVENTS } from "@src/constants/firebaseEvents";
import { Qualification } from "@src/appV2/Admin/Qualifications/types";
import { handleApiError } from "src/utils/errors";

interface BaseChargeProps {
  hcfId: string;
  hcfType: string;
  qualifications: Qualification[];
  updateFacility: (data: { facilityId: string; rates: Record<string, number> }) => Promise<void>;
}

interface WorkerChargeRate {
  workerType?: string;
  baseChargeRate?: number;
  facilityId?: string;
}

export function BaseCharge(props: BaseChargeProps) {
  const { hcfId, updateFacility, hcfType, qualifications } = props;
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [details, setDetails] = useState<any>();
  const [error, setError] = useState();

  const qualificationsWithRates = qualifications.filter(
    (qualification) => qualification.attributes.name !== "NURSE"
  );

  useEffect(() => {
    fetchHcfDetails(hcfId, "type, rates").then(setDetails).catch(setError);
  }, [hcfId, hcfType]);

  const updateLocal = (data) => {
    // `data` is undefined if saveFacility's retrieveUser refresh failed
    // (swallowed there). The optimistic update already reflects the
    // submitted value, so leave local state alone in that case.
    if (!data?.facility?.rates) {
      return;
    }
    setDetails((prev) => ({ ...prev, rates: data.facility.rates }));
  };

  const handleRateChange = (parameters: WorkerChargeRate) => {
    const { workerType, baseChargeRate, facilityId } = parameters;

    if (!workerType || typeof baseChargeRate !== "number" || !facilityId) {
      return;
    }

    const previousRate = details?.rates?.[workerType];

    setDetails((prev) => ({
      ...prev,
      rates: { ...prev?.rates, [workerType]: baseChargeRate },
    }));

    updateFacility({
      facilityId: hcfId,
      rates: { [workerType]: baseChargeRate },
    })
      .then(updateLocal)
      .then(() =>
        showSuccessToast(
          `Base charge rate for ${workerType} has been updated to $${baseChargeRate}.`
        )
      )
      .catch((error) => {
        handleApiError(error);
        setDetails((prev) => ({
          ...prev,
          rates: { ...prev?.rates, [workerType]: previousRate },
        }));
      });
  };

  return (
    <Stack>
      <Stack flexDirection="row">
        <Text variant="body2" bold>
          HCF Base Charge
        </Text>
        <Tooltip title="This is agreed upon rate for each HCP type.">
          <HelpOutlineIcon fontSize="small" />
        </Tooltip>
      </Stack>
      <div className="content">
        <ShowError error={error} message="Unable to fetch workplace Base Charge" />
        <WorkerRate
          qualifications={qualificationsWithRates}
          rates={details?.rates}
          onChange={(data) => {
            const workerType = Object.keys(data)[0];
            const newChargeRate = {
              facilityId: hcfId,
              workerType,
              baseChargeRate: data[workerType],
            };
            logEvent(ADMIN_EVENTS.FACILITY_CHARGE_RATE_CHANGED, {
              ...newChargeRate,
              oldBaseChargeRate: details?.rates?.[workerType],
            });
            handleRateChange(newChargeRate);
          }}
        />
      </div>
    </Stack>
  );
}
